import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  LuArrowLeft, LuTrash2, LuCode, LuRefreshCw, LuPlus, LuSend, LuFileText
} from "react-icons/lu";
import { FaWhatsapp } from "react-icons/fa";
import { API } from "../App";

const emptyMetaTemplateForm = { name: "", display_name: "", category: "UTILITY", language: "es", header_text: "", body_text: "", footer_text: "" };

const TemplatesPage = ({ auth }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addTemplateOpen, setAddTemplateOpen] = useState(false);
  const [createMetaTemplateOpen, setCreateMetaTemplateOpen] = useState(false);
  const [sendTemplateOpen, setSendTemplateOpen] = useState(false);
  const [tplSaving, setTplSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    template_name: "", language_code: "es", display_name: "", body_text: "", parameters_count: 0
  });
  const [metaTemplateForm, setMetaTemplateForm] = useState(emptyMetaTemplateForm);
  const [sendForm, setSendForm] = useState({ phone_number: "", template_name: "", language_code: "es", parameters: "" });

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API}/whatsapp/templates`, { headers: { Authorization: `Bearer ${auth.token}` } });
      if (res.ok) setTemplates(await res.json());
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplate = async (e) => {
    e.preventDefault();
    setTplSaving(true);
    const res = await fetch(`${API}/whatsapp/templates`, {
      method: "POST",
      headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ ...templateForm, parameters_count: Number(templateForm.parameters_count) })
    });
    if (res.ok) {
      toast.success("Plantilla registrada");
      setAddTemplateOpen(false);
      setTemplateForm({ template_name: "", language_code: "es", display_name: "", body_text: "", parameters_count: 0 });
      fetchTemplates();
    } else {
      const err = await res.json();
      toast.error(err.detail || "Error al registrar plantilla");
    }
    setTplSaving(false);
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("¿Eliminar esta plantilla?")) return;
    const res = await fetch(`${API}/whatsapp/templates/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${auth.token}` } });
    if (res.ok) { toast.success("Plantilla eliminada"); fetchTemplates(); }
  };

  const handleCreateMetaTemplate = async (e) => {
    e.preventDefault();
    setTplSaving(true);
    try {
      const res = await fetch(`${API}/whatsapp/create-meta-template`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
        body: JSON.stringify(metaTemplateForm)
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Plantilla enviada a Meta para aprobación");
        setCreateMetaTemplateOpen(false);
        setMetaTemplateForm(emptyMetaTemplateForm);
        fetchTemplates();
      } else {
        toast.error(data.detail || "Error al crear plantilla");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setTplSaving(false);
    }
  };

  const handleSyncMetaTemplates = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/whatsapp/sync-meta-templates`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Plantillas sincronizadas");
        fetchTemplates();
      } else {
        toast.error(data.detail || "Error al sincronizar");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setSyncing(false);
    }
  };

  const handleSendTemplate = async (e) => {
    e.preventDefault();
    setTplSaving(true);
    const params = sendForm.parameters ? sendForm.parameters.split("|").map(p => p.trim()).filter(Boolean) : [];
    const res = await fetch(`${API}/whatsapp/send-template`, {
      method: "POST",
      headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ phone_number: sendForm.phone_number, template_name: sendForm.template_name, language_code: sendForm.language_code, parameters: params })
    });
    if (res.ok) {
      toast.success("Plantilla enviada correctamente");
      setSendTemplateOpen(false);
      setSendForm({ phone_number: "", template_name: "", language_code: "es", parameters: "" });
    } else {
      const err = await res.json();
      toast.error(err.detail || "Error al enviar plantilla");
    }
    setTplSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" className="rounded-xl" data-testid="back-btn">
                <LuArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <LuFileText className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg text-foreground">Plantillas de WhatsApp</h1>
                <p className="text-xs text-muted-foreground">Crea, sincroniza y envía plantillas aprobadas por Meta</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass-card border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="flex items-center gap-3 text-foreground">
                  <FaWhatsapp className="w-5 h-5 text-green-500" />
                  Plantillas de mensajes
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" className="rounded-full gap-2" onClick={handleSyncMetaTemplates} disabled={syncing}>
                    <LuRefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                    {syncing ? "Sincronizando..." : "Sincronizar desde Meta"}
                  </Button>

                  <Dialog open={sendTemplateOpen} onOpenChange={setSendTemplateOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="icon" className="rounded-xl border-white/20 shrink-0" title="Enviar plantilla">
                        <LuSend className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border max-w-md">
                      <DialogHeader><DialogTitle className="font-heading text-foreground">Enviar plantilla de WhatsApp</DialogTitle></DialogHeader>
                      <form onSubmit={handleSendTemplate} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Número de teléfono</Label>
                          <Input placeholder="34612345678" className="bg-muted/50 border-transparent rounded-xl font-mono"
                            value={sendForm.phone_number} onChange={e => setSendForm({ ...sendForm, phone_number: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Plantilla</Label>
                          <select className="w-full bg-muted/50 border border-transparent rounded-xl px-3 py-2 text-sm text-foreground"
                            value={sendForm.template_name} onChange={e => { const t = templates.find(t => t.template_name === e.target.value); setSendForm({ ...sendForm, template_name: e.target.value, language_code: t?.language_code || "es" }); }} required>
                            <option value="">Selecciona una plantilla...</option>
                            {templates.map(t => <option key={t.id} value={t.template_name}>{t.display_name}</option>)}
                          </select>
                        </div>
                        {sendForm.template_name && templates.find(t => t.template_name === sendForm.template_name)?.parameters_count > 0 && (
                          <div className="space-y-2">
                            <Label className="text-foreground">Parámetros (separados por |)</Label>
                            <Input placeholder="Valor1 | Valor2" className="bg-muted/50 border-transparent rounded-xl"
                              value={sendForm.parameters} onChange={e => setSendForm({ ...sendForm, parameters: e.target.value })} />
                          </div>
                        )}
                        <div className="flex justify-end gap-3 pt-2">
                          <Button type="button" variant="ghost" onClick={() => setSendTemplateOpen(false)}>Cancelar</Button>
                          <Button type="submit" className="rounded-full" disabled={tplSaving}><FaWhatsapp className="w-4 h-4 mr-2" />{tplSaving ? "Enviando..." : "Enviar"}</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>

                  {/* Crear plantilla en Meta */}
                  <Dialog open={createMetaTemplateOpen} onOpenChange={setCreateMetaTemplateOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700 gap-2">
                        <LuPlus className="w-4 h-4" />Crear en Meta
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="font-heading text-foreground">Crear plantilla en Meta</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateMetaTemplate} className="space-y-4 mt-2">
                        <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-xs text-muted-foreground">
                          La plantilla se enviará a Meta para su <strong className="text-foreground">revisión y aprobación</strong>. Las plantillas UTILITY suelen aprobarse en minutos.
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-foreground text-sm">Nombre interno</Label>
                            <Input
                              placeholder="bienvenida_lead"
                              className="bg-muted/50 border-transparent rounded-xl font-mono text-sm"
                              value={metaTemplateForm.name}
                              onChange={e => setMetaTemplateForm({ ...metaTemplateForm, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                              required
                            />
                            <p className="text-[10px] text-muted-foreground">Solo minúsculas y guiones bajos</p>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-foreground text-sm">Nombre para mostrar</Label>
                            <Input
                              placeholder="Bienvenida al lead"
                              className="bg-muted/50 border-transparent rounded-xl text-sm"
                              value={metaTemplateForm.display_name}
                              onChange={e => setMetaTemplateForm({ ...metaTemplateForm, display_name: e.target.value })}
                              required
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-foreground text-sm">Categoría</Label>
                            <select
                              className="w-full bg-muted/50 border border-transparent rounded-xl px-3 py-2 text-sm text-foreground"
                              value={metaTemplateForm.category}
                              onChange={e => setMetaTemplateForm({ ...metaTemplateForm, category: e.target.value })}
                            >
                              <option value="UTILITY">UTILITY — Transaccional</option>
                              <option value="MARKETING">MARKETING — Promocional</option>
                            </select>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-foreground text-sm">Idioma</Label>
                            <select
                              className="w-full bg-muted/50 border border-transparent rounded-xl px-3 py-2 text-sm text-foreground"
                              value={metaTemplateForm.language}
                              onChange={e => setMetaTemplateForm({ ...metaTemplateForm, language: e.target.value })}
                            >
                              <option value="es">Español (es)</option>
                              <option value="es_ES">Español España (es_ES)</option>
                              <option value="en_US">Inglés (en_US)</option>
                              <option value="pt_BR">Portugués (pt_BR)</option>
                            </select>
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-foreground text-sm">Encabezado <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                          <Input
                            placeholder="Texto del encabezado"
                            className="bg-muted/50 border-transparent rounded-xl text-sm"
                            value={metaTemplateForm.header_text}
                            onChange={e => setMetaTemplateForm({ ...metaTemplateForm, header_text: e.target.value })}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-foreground text-sm">Cuerpo del mensaje <span className="text-red-400">*</span></Label>
                          <textarea
                            rows={4}
                            placeholder={"Hola {{1}}, te contactamos desde Eniit.\n\nTenemos una oferta personalizada para ti. ¿Tienes unos minutos?"}
                            className="w-full bg-muted/50 border border-transparent rounded-xl px-3 py-2 text-sm text-foreground resize-none focus:outline-none focus:border-primary"
                            value={metaTemplateForm.body_text}
                            onChange={e => setMetaTemplateForm({ ...metaTemplateForm, body_text: e.target.value })}
                            required
                          />
                          <p className="text-[10px] text-muted-foreground">Usa {`{{1}}`}, {`{{2}}`}... para variables personalizables</p>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-foreground text-sm">Pie de página <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                          <Input
                            placeholder="Responde STOP para no recibir más mensajes"
                            className="bg-muted/50 border-transparent rounded-xl text-sm"
                            value={metaTemplateForm.footer_text}
                            onChange={e => setMetaTemplateForm({ ...metaTemplateForm, footer_text: e.target.value })}
                          />
                        </div>

                        <div className="flex justify-end gap-3 pt-1">
                          <Button type="button" variant="ghost" onClick={() => setCreateMetaTemplateOpen(false)}>Cancelar</Button>
                          <Button type="submit" className="rounded-full bg-green-600 hover:bg-green-700" disabled={tplSaving}>
                            {tplSaving ? "Enviando a Meta..." : "Enviar para aprobación"}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={addTemplateOpen} onOpenChange={setAddTemplateOpen}>
                    <DialogTrigger asChild>
                      <Button className="rounded-full bg-primary hover:bg-primary/90" size="sm">
                        <LuPlus className="w-4 h-4 mr-2" />Registrar plantilla
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border max-w-lg">
                      <DialogHeader><DialogTitle className="font-heading text-foreground">Registrar plantilla de WhatsApp</DialogTitle></DialogHeader>
                      <form onSubmit={handleAddTemplate} className="space-y-4 mt-4">
                        <div className="p-3 rounded-xl bg-primary/10 border border-primary/20 text-xs text-muted-foreground">
                          La plantilla debe estar <strong className="text-foreground">aprobada en Meta for Developers</strong> antes de registrarla aquí.
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-foreground">Nombre en Meta</Label>
                            <Input placeholder="bienvenida_lead" className="bg-muted/50 border-transparent rounded-xl font-mono text-sm"
                              value={templateForm.template_name} onChange={e => setTemplateForm({ ...templateForm, template_name: e.target.value })} required />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-foreground">Idioma</Label>
                            <select className="w-full bg-muted/50 border border-transparent rounded-xl px-3 py-2 text-sm text-foreground"
                              value={templateForm.language_code} onChange={e => setTemplateForm({ ...templateForm, language_code: e.target.value })}>
                              <option value="es">Español (es)</option>
                              <option value="es_ES">Español España (es_ES)</option>
                              <option value="en_US">Inglés (en_US)</option>
                              <option value="pt_BR">Portugués (pt_BR)</option>
                            </select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Nombre para mostrar</Label>
                          <Input placeholder="Bienvenida al lead" className="bg-muted/50 border-transparent rounded-xl"
                            value={templateForm.display_name} onChange={e => setTemplateForm({ ...templateForm, display_name: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Texto del cuerpo</Label>
                          <textarea rows={3} placeholder="Hola {{1}}, gracias por tu interés..."
                            className="w-full bg-muted/50 border border-transparent rounded-xl px-3 py-2 text-sm text-foreground resize-none focus:outline-none focus:border-primary"
                            value={templateForm.body_text} onChange={e => setTemplateForm({ ...templateForm, body_text: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-foreground">Número de parámetros</Label>
                          <Input type="number" min={0} max={10} className="bg-muted/50 border-transparent rounded-xl w-24"
                            value={templateForm.parameters_count} onChange={e => setTemplateForm({ ...templateForm, parameters_count: e.target.value })} />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                          <Button type="button" variant="ghost" onClick={() => setAddTemplateOpen(false)}>Cancelar</Button>
                          <Button type="submit" className="rounded-full bg-primary" disabled={tplSaving}>{tplSaving ? "Guardando..." : "Registrar"}</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="p-8 text-center border border-dashed border-white/10 rounded-xl">
                  <LuCode className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">No hay plantillas registradas.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map((t, i) => (
                    <motion.div key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                      className="p-4 rounded-xl border border-white/10 bg-muted/20">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-foreground">{t.display_name}</p>
                            <Badge className="bg-muted text-muted-foreground border-white/10 font-mono text-xs">{t.template_name}</Badge>
                            <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">{t.language_code}</Badge>
                            {t.category && <Badge className="bg-muted text-muted-foreground border-white/10 text-xs">{t.category}</Badge>}
                            {t.parameters_count > 0 && <Badge className="bg-muted text-muted-foreground border-white/10 text-xs">{t.parameters_count} params</Badge>}
                            {t.meta_status === "PENDING" && <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">Pendiente aprobación</Badge>}
                            {t.meta_status === "REJECTED" && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">Rechazada</Badge>}
                            {t.meta_status === "APPROVED" && <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">Aprobada</Badge>}
                          </div>
                          {t.header_text && <p className="text-xs text-muted-foreground mt-1 font-semibold">{t.header_text}</p>}
                          <p className="text-sm text-muted-foreground mt-2 bg-muted/30 rounded-lg px-3 py-2 font-mono">{t.body_text}</p>
                          {t.footer_text && <p className="text-xs text-muted-foreground mt-1 italic">{t.footer_text}</p>}
                        </div>
                        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive shrink-0" onClick={() => handleDeleteTemplate(t.id)}>
                          <LuTrash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default TemplatesPage;
